import { useLang } from 'hooks/lang';

import { actions, QuotationRequest, quotationRequestConfig } from 'entities';

import { Column, Row, Modal, LabeledTextarea, LabeledSelect, Button } from 'components/Blocks';
import { EntitySelect } from 'components/Entities';
import { CircleXIcon, ReceiptLongIcon } from 'components/Icons';

import { DiscountRequest } from './Discount';
import { SampleIds } from './SampleIds';
import { ProjectInvoiceValue } from '../ProjectInvoiceValue';
import { InlineAlert } from '../InlineAlert';

import { useProjectProfile } from 'pages/Projects/ProjectProfile/context';
import { useFormControl, useWatch } from 'hooks';

import { calculateInvoiceValues, validateCanCreate } from './utils';

import { INVOICE_COUNTRIES } from 'utils/countries';
import { formatCurrency } from 'utils/text';

import { InvoiceControlCreation } from './types';

interface CreateInvoiceModalProps {
  quotationRequest: QuotationRequest | null;
  isOpen: boolean;
  close: () => void;
}

export function ProjectInvoiceCreateModal({
  quotationRequest,
  close,
  isOpen,
}: CreateInvoiceModalProps): JSX.Element | null {
  const { t } = useLang();

  const { project, updateProject } = useProjectProfile();

  const { state, dispatch, handleChange, resetDefault } = useFormControl<InvoiceControlCreation>({
    client: project.client,
    country: '',
    extraInformation: '',
    sampleIds: [],
    value: '',
  });

  useWatch(
    quotationRequest,
    () =>
      quotationRequest &&
      dispatch({
        quotationRequest: quotationRequest.id,
        value: quotationRequest.value,
        client: quotationRequest.client,
        country: quotationRequest.country,
      }),
  );

  const { total } = calculateInvoiceValues(state);

  const canCreate = validateCanCreate(project, state, quotationRequest);

  const executeClosure = (): void => {
    close();
    resetDefault();
  };

  const createInvoice = actions.invoice.createProjectInvoice.use({
    block: !canCreate,

    params: {
      ...state,
      project: project.id,
      quotationRequest: quotationRequest?.id,
    },

    onSuccess: (created) => {
      updateProject((old) => ({
        ...old,

        invoice: {
          ...old.invoice,

          projectInvoices: [...(old.invoice.projectInvoices || []), created],

          quotationRequests: old.invoice.quotationRequests.map((request) =>
            request.id === quotationRequest?.id
              ? {
                  ...request,
                  status: quotationRequestConfig.associated.code,
                  associatedProjectInvoice: created.id,
                }
              : request,
          ),
        },
      }));

      executeClosure();
    },
  });

  return (
    <>
      <Modal
        title={t('create-project-invoice')}
        isOpen={isOpen}
        close={executeClosure}
        allowEscape={false}
        width="min(90vw, 790px)"
        gap="6"
        className="overflow-y-auto"
      >
        <EntitySelect
          target="clients.list"
          labelKey="name"
          value={state.client}
          onChange={handleChange}
          id="client"
          label={t('client')}
          disabled={!!quotationRequest}
        />

        <Row alignItems="stretch" className="phone:flex-col">
          <LabeledSelect
            options={INVOICE_COUNTRIES}
            value={state.country}
            onChangeDirect={(country) => dispatch({ country })}
            label={t('country') as string}
            disabled={!!quotationRequest}
            containerClassName="min-w-[35%]"
          />

          <ProjectInvoiceValue
            currency={quotationRequest?.currency}
            state={state}
            dispatch={dispatch}
            disabled={!!quotationRequest}
          />
        </Row>

        <LabeledTextarea
          label={t('extra-information')}
          value={state.extraInformation}
          placeholder={t('type-here-a-comment')!}
          id="extraInformation"
          onChange={handleChange}
          containerClassName="-mt-5 phone:mt-0"
        />

        <DiscountRequest
          key={`${isOpen}`}
          state={state}
          dispatch={dispatch}
          currency={project.currency}
        />

        <SampleIds isOpen={isOpen} state={state} dispatch={dispatch} />

        <Column
          gap="2"
          alignItems="center"
          className="rounded-md border border-primary/50 bg-primary/15 p-4"
        >
          <span className="font-semibold text-primary">Total</span>

          <div className="w-fit rounded-full bg-primary px-3 py-1.5 text-xl font-medium text-primary-foreground">
            <span className="mr-2 text-sm">{quotationRequest?.currency ?? project.currency}</span>{' '}
            {formatCurrency(total ?? 0)}
          </div>

          {!!quotationRequest && (
            <span className="text-end text-xs text-primary">
              ({project.currency} {formatCurrency(quotationRequest.valueInProjectCurrency)})
            </span>
          )}
        </Column>

        {quotationRequest && (
          <InlineAlert
            title={t('attention')}
            description={t(
              'this-invoice-has-a-quotation-request-as-reference-if-any-value-needs-changing-a-new-request-is-required',
            )}
          />
        )}

        <Row justifyContent="space-between" className="mt-2 phone:flex-col phone:items-stretch">
          <Button variant="outlined" onClick={executeClosure}>
            <CircleXIcon />
            {t('cancel')}
          </Button>

          <Button disabled={!canCreate} {...createInvoice.buttonProps}>
            <ReceiptLongIcon />

            {state.discountRequest
              ? t('request-creation-with-discount')
              : t('create-project-invoice')}
          </Button>
        </Row>
      </Modal>
    </>
  );
}
